import isEmpty from 'lodash-es/isEmpty'
import * as React from 'react'

import itemAspectRatio from '../../../../common/utils/itemAspectRatio'
import { IItem } from '../../../../typings/searchResults'
import ItemCard from '../../itemCard'
import Scroll from '../../itemCard/photoduneGridItemCard/scrollContainer'
import ItemDetails from '../../itemDetails/itemDetailsContainer'
import { buildItemClassName } from './buildItemClassName'
import GridStyles from './gridStyles'
import { balanceRows } from './rowBalancer'
import WithItemDecorator from '../../../containers/withItemDecorator'

import * as styles from './grid.module.css'

interface IProps {
  items: IItem[]
  location: any
  selectedItemId: number
}

export interface IItemRow {
  id: number
  items: IItem[]
  size: number
}

const splitItemsIntoRows = (items: IItem[], itemsPerRow: number) => {
  if (isEmpty(items)) {
    return null
  }

  const rows = items.reduce<IItemRow[]>((accumulator, item) => {
    const currentRow = accumulator[accumulator.length - 1]
    const canAddToRow = accumulator.length > 0 && currentRow.size < itemsPerRow

    const aspectRatio = itemAspectRatio(item) || 1.0

    if (canAddToRow) {
      currentRow.items.push(item)
      currentRow.size += aspectRatio
      return accumulator
    }

    return [
      ...accumulator,
      {
        id: item.id,
        items: [item],
        size: aspectRatio
      }
    ]
  }, [])

  return balanceRows(rows, itemsPerRow)
}

const renderItems = (items: IItem[], location: any, shouldScroll: boolean) => {
  return items
    .filter(item => item.image_urls && item.image_urls.length > 0)
    .map((item, i) => {
      const itemClassNames = [
        buildItemClassName('item', item.id),
        styles.item
      ].join(' ')

      const itemDetailsClassName = buildItemClassName('details', item.id)

      const prevItem = items[i - 1]
      const nextItem = items[i + 1]

      return (
        <div key={i} className={itemClassNames} data-test="gridItem">
          <Scroll itemId={item.id} shouldScroll={shouldScroll}>
            <ItemCard item={item} index={i} imageClassName={styles.image} />
            <WithItemDecorator
              render={itemDecorator => {
                const itemProps = itemDecorator(item, i)
                return (
                  <ItemDetails
                    itemDetailsClassName={itemDetailsClassName}
                    prevItem={prevItem}
                    nextItem={nextItem}
                    location={location}
                    {...itemProps}
                  />
                )
              }}
            />
          </Scroll>
        </div>
      )
    })
}

class Grid extends React.Component<IProps> {
  private shouldScroll = true

  public componentWillUpdate(nextProps: IProps) {
    this.shouldScroll = this.props.selectedItemId !== nextProps.selectedItemId
  }

  public render() {
    const { items, location } = this.props

    return (
      <div className={styles.gridContainer}>
        <GridStyles
          rows={splitItemsIntoRows(items, 1)}
          itemsPerRow={1}
          minWidth={0}
          maxWidth={719}
        />
        <GridStyles
          rows={splitItemsIntoRows(items, 2)}
          itemsPerRow={2}
          minWidth={720}
          maxWidth={999}
        />
        <GridStyles
          rows={splitItemsIntoRows(items, 3)}
          itemsPerRow={3}
          minWidth={1000}
          maxWidth={1199}
        />
        <GridStyles
          rows={splitItemsIntoRows(items, 4)}
          itemsPerRow={4}
          minWidth={1200}
          maxWidth={0}
        />
        {renderItems(items, location, this.shouldScroll)}
      </div>
    )
  }
}

export default Grid
